import React, {
  ClipboardEvent,
  forwardRef,
  KeyboardEvent,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from 'static';
import { CommunityUser } from 'types';
import { getProfileName } from 'utils/common';

import {
  formatOtherRecipients,
  MAX_OTHER_RECIPIENTS,
  OTHER_RECIPIENTS_EMAIL_REGEX,
  parseOtherRecipients,
} from '../../otherRecipients';
import RecipientAvatar from './RecipientAvatar';
import {
  StyledChip,
  StyledChipLabel,
  StyledChipRemove,
  StyledChips,
  StyledFieldError,
  StyledFieldLabel,
  StyledFieldRow,
  StyledInput,
  StyledMoreChip,
  StyledOption,
  StyledOptionEmail,
  StyledOptionEmpty,
  StyledOptionName,
  StyledOptionText,
  StyledSuggestions,
  StyledWrapper,
} from './styled';

const COLLAPSED_CHIP_LIMIT = 8;
const MAX_SUGGESTIONS = 50;

type Option =
  | { type: 'member'; user: CommunityUser }
  | { type: 'external'; email: string };

type Chip =
  | { type: 'member'; id: string; name: string; email: string; logo?: string }
  | { type: 'external'; email: string };

const isValidEmail = (value: string) =>
  OTHER_RECIPIENTS_EMAIL_REGEX.test(value);

export interface RecipientsFieldHandle {
  focus: () => void;
  clear: () => void;
}

interface RecipientsFieldProps {
  inputId: string;
  users: CommunityUser[];
  selectedIds: string[];
  otherRecipients: string;
  maxMembers: number;
  error?: string;
  onSelectedIdsChange: (ids: string[]) => void;
  onOtherRecipientsChange: (value: string) => void;
  onOtherRecipientsLimit: (exceeded: boolean) => void;
}

const RecipientsField = forwardRef<RecipientsFieldHandle, RecipientsFieldProps>(
  (
    {
      inputId,
      users,
      selectedIds,
      otherRecipients,
      maxMembers,
      error,
      onSelectedIdsChange,
      onOtherRecipientsChange,
      onOtherRecipientsLimit,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(0);
    const [expanded, setExpanded] = useState(false);
    const [inputError, setInputError] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
        setIsOpen(true);
      },
      clear: () => {
        setQuery('');
        setInputError(null);
        setIsOpen(false);
        setHighlighted(0);
        setExpanded(false);
      },
    }));

    const usersById = useMemo(
      () => new Map(users.map((user) => [user.id, user])),
      [users],
    );
    const usersByEmail = useMemo(
      () =>
        new Map(
          users
            .filter((user) => user.profile?.email)
            .map((user) => [user.profile.email.toLowerCase(), user]),
        ),
      [users],
    );
    const externalEmails = useMemo(
      () => parseOtherRecipients(otherRecipients),
      [otherRecipients],
    );

    const chips: Chip[] = useMemo(
      () => [
        ...selectedIds
          .map((id) => usersById.get(id))
          .filter(Boolean)
          .map(
            (user): Chip => ({
              type: 'member',
              id: user.id,
              name: getProfileName(user.profile || {}),
              email: user.profile?.email || '',
              logo: user.profile?.smallLogo || user.profile?.logo,
            }),
          ),
        ...externalEmails.map((email): Chip => ({ type: 'external', email })),
      ],
      [selectedIds, usersById, externalEmails],
    );

    const options: Option[] = useMemo(() => {
      const needle = query.trim().toLowerCase();
      const selected = new Set(selectedIds);
      const members = users
        .filter((user) => !selected.has(user.id))
        .filter((user) => {
          if (!needle) return true;
          const name = getProfileName(user.profile || {}).toLowerCase();
          const email = (user.profile?.email || '').toLowerCase();
          return name.includes(needle) || email.includes(needle);
        })
        .slice(0, MAX_SUGGESTIONS)
        .map((user): Option => ({ type: 'member', user }));

      const trimmed = query.trim();
      const canAddExternal =
        isValidEmail(trimmed) &&
        !usersByEmail.has(trimmed.toLowerCase()) &&
        !externalEmails.some((e) => e.toLowerCase() === trimmed.toLowerCase());

      return canAddExternal
        ? [{ type: 'external', email: trimmed }, ...members]
        : members;
    }, [query, users, selectedIds, usersByEmail, externalEmails]);

    useEffect(() => {
      setHighlighted(0);
    }, [query]);

    useEffect(() => {
      listRef.current
        ?.querySelector<HTMLElement>(`[data-index="${highlighted}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    }, [highlighted]);

    const addMembers = (ids: string[]) => {
      const next = [...new Set([...selectedIds, ...ids])].slice(0, maxMembers);
      onSelectedIdsChange(next);
    };

    const setExternal = (emails: string[]) => {
      const exceeded = emails.length > MAX_OTHER_RECIPIENTS;
      onOtherRecipientsLimit(exceeded);
      onOtherRecipientsChange(
        formatOtherRecipients(emails.slice(0, MAX_OTHER_RECIPIENTS)),
      );
    };

    /** Turns typed or pasted text into chips; returns the tokens it could not use. */
    const commitText = (text: string): string[] => {
      const tokens = text.split(/[\s,;]+/).filter(Boolean);
      if (!tokens.length) return [];

      const memberIds: string[] = [];
      const nextExternal = [...externalEmails];
      const invalid: string[] = [];

      tokens.forEach((token) => {
        const member = usersByEmail.get(token.toLowerCase());
        if (member) {
          memberIds.push(member.id);
        } else if (isValidEmail(token)) {
          if (
            !nextExternal.some((e) => e.toLowerCase() === token.toLowerCase())
          ) {
            nextExternal.push(token);
          }
        } else {
          invalid.push(token);
        }
      });

      if (memberIds.length) addMembers(memberIds);
      if (nextExternal.length !== externalEmails.length) {
        setExternal(nextExternal);
      }
      setInputError(
        invalid.length
          ? t('email.otherRecipientsInvalid', { email: invalid[0] })
          : null,
      );
      return invalid;
    };

    const selectOption = (option: Option) => {
      if (option.type === 'member') {
        addMembers([option.user.id]);
      } else {
        setExternal([...externalEmails, option.email]);
      }
      setQuery('');
      setInputError(null);
      inputRef.current?.focus();
    };

    const removeChip = (chip: Chip) => {
      if (chip.type === 'member') {
        onSelectedIdsChange(selectedIds.filter((id) => id !== chip.id));
      } else {
        setExternal(externalEmails.filter((email) => email !== chip.email));
      }
      inputRef.current?.focus();
    };

    const commitQuery = () => {
      if (!query.trim()) return;
      setQuery(commitText(query).join(' '));
    };

    const handleQueryChange = (value: string) => {
      setInputError(null);

      const hasSeparator =
        /[,;]/.test(value) || (value.includes('@') && /\s/.test(value));
      if (!hasSeparator) {
        setIsOpen(true);
        setQuery(value);
        return;
      }

      const tokens = value.split(/[,;\s]+/).filter(Boolean);
      const pending = /[,;\s]$/.test(value) ? '' : tokens.pop() || '';
      const invalid = commitText(tokens.join(' '));
      const nextQuery = [...invalid, pending].join(' ').trimStart();
      setQuery(nextQuery);
      setIsOpen(!!nextQuery);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setIsOpen(true);
          setHighlighted((index) => Math.min(index + 1, options.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlighted((index) => Math.max(index - 1, 0));
          break;
        case 'Enter':
          if (event.metaKey || event.ctrlKey) break;
          event.preventDefault();
          if (isOpen && query.trim() && options[highlighted]) {
            selectOption(options[highlighted]);
          } else {
            commitQuery();
          }
          break;
        case 'Tab':
          if (query.includes('@')) commitQuery();
          setIsOpen(false);
          break;
        case 'Escape':
          setIsOpen(false);
          break;
        case 'Backspace':
          if (!query && chips.length) {
            removeChip(chips[chips.length - 1]);
          }
          break;
        default:
          break;
      }
    };

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
      const text = event.clipboardData.getData('text/plain');
      if (!text.includes('@')) return;
      event.preventDefault();
      setQuery(commitText(`${query} ${text}`).join(' '));
    };

    const handleBlur = () => {
      if (query.includes('@')) commitQuery();
      setIsOpen(false);
    };

    const visibleChips =
      expanded || chips.length <= COLLAPSED_CHIP_LIMIT
        ? chips
        : chips.slice(0, COLLAPSED_CHIP_LIMIT);
    const hiddenCount = chips.length - visibleChips.length;
    const showSuggestions = isOpen && (options.length > 0 || !!query.trim());
    const listboxId = `${inputId}-suggestions`;
    const displayError = inputError || error;

    return (
      <StyledWrapper>
        <StyledFieldRow
          hasError={!!displayError}
          onMouseDown={(event) => {
            const target = event.target as HTMLElement;
            if (!target.closest('button, input, [data-chip]')) {
              event.preventDefault();
              inputRef.current?.focus();
              setIsOpen(true);
            }
          }}
        >
          <StyledFieldLabel htmlFor={inputId}>{t('email.to')}</StyledFieldLabel>
          <StyledChips expanded={expanded && hiddenCount === 0}>
            {visibleChips.map((chip) => {
              const key = chip.type === 'member' ? chip.id : chip.email;
              const label = chip.type === 'member' ? chip.name : chip.email;
              return (
                <StyledChip
                  key={`${chip.type}-${key}`}
                  data-chip
                  title={chip.type === 'member' ? chip.email : undefined}
                  external={chip.type === 'external'}
                >
                  <RecipientAvatar
                    seed={key}
                    name={label}
                    src={chip.type === 'member' ? chip.logo : null}
                  />
                  <StyledChipLabel>{label}</StyledChipLabel>
                  <StyledChipRemove
                    type="button"
                    aria-label={t('email.removeRecipient', { name: label })}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => removeChip(chip)}
                  >
                    <CloseIcon />
                  </StyledChipRemove>
                </StyledChip>
              );
            })}
            {hiddenCount > 0 && (
              <StyledMoreChip type="button" onClick={() => setExpanded(true)}>
                {t('email.moreRecipients', { count: hiddenCount })}
              </StyledMoreChip>
            )}
            {expanded && chips.length > COLLAPSED_CHIP_LIMIT && (
              <StyledMoreChip type="button" onClick={() => setExpanded(false)}>
                {t('email.showLess')}
              </StyledMoreChip>
            )}
            <StyledInput
              id={inputId}
              ref={inputRef}
              value={query}
              autoComplete="off"
              role="combobox"
              aria-expanded={showSuggestions}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={
                showSuggestions ? `${listboxId}-${highlighted}` : undefined
              }
              placeholder={chips.length ? '' : t('email.toPlaceholder')}
              onChange={(event) => handleQueryChange(event.target.value)}
              onFocus={() => setIsOpen(true)}
              onClick={() => setIsOpen(true)}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
            />
          </StyledChips>
        </StyledFieldRow>
        {showSuggestions && (
          <StyledSuggestions id={listboxId} role="listbox" ref={listRef}>
            {options.length === 0 && (
              <StyledOptionEmpty>{t('email.noMembersFound')}</StyledOptionEmpty>
            )}
            {options.map((option, index) => {
              const isMember = option.type === 'member';
              const name = isMember
                ? getProfileName(option.user.profile || {})
                : option.email;
              const seed = isMember ? option.user.id : option.email;
              return (
                <StyledOption
                  key={`${option.type}-${seed}`}
                  id={`${listboxId}-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={index === highlighted}
                  highlighted={index === highlighted}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setHighlighted(index)}
                  onClick={() => selectOption(option)}
                >
                  <RecipientAvatar
                    seed={seed}
                    name={name}
                    size={30}
                    src={
                      isMember
                        ? option.user.profile?.smallLogo ||
                          option.user.profile?.logo
                        : null
                    }
                  />
                  <StyledOptionText>
                    <StyledOptionName>
                      {isMember
                        ? name
                        : t('email.addAddress', { email: option.email })}
                    </StyledOptionName>
                    {isMember && option.user.profile?.email && (
                      <StyledOptionEmail>
                        {option.user.profile.email}
                      </StyledOptionEmail>
                    )}
                  </StyledOptionText>
                </StyledOption>
              );
            })}
          </StyledSuggestions>
        )}
        {displayError && <StyledFieldError>{displayError}</StyledFieldError>}
      </StyledWrapper>
    );
  },
);

RecipientsField.displayName = 'RecipientsField';

export default RecipientsField;
